import { useMemo, useState } from "react";
import { Plus, Trash2, User, X, Search } from "lucide-react";
import { useAuth } from "../../../app/AuthContext";
import { useSchoolProfile } from "../../../app/SchoolProfileContext";
import { type StudentData as ApiStudentData } from "../../../lib/api";
import { useStudents, useCreateStudent } from "../../../hooks/useStudents";
import { useEnrollments, useEnrollStudent, useDeleteEnrollment } from "../../../hooks/useEnrollments";

export interface Student extends ApiStudentData {
  name: string; // Map full_name to name
}

interface StudentsManagerProps {
  className: string;
  classId: string;
  onClose: () => void;
  onRefresh?: () => void;
}

export default function StudentsManager({
  className,
  classId,
  onClose,
  onRefresh,
}: StudentsManagerProps) {
  const { token } = useAuth();
  const { activeProfile } = useSchoolProfile();

  // React Query Hooks
  const { data: allStudents = [], isLoading: studentsLoading } = useStudents(token, activeProfile?.id);
  const { data: allEnrollments = [], isLoading: enrollmentsLoading } = useEnrollments(token, activeProfile?.id);

  const createStudentMutation = useCreateStudent(token!, activeProfile?.id);
  const enrollStudentMutation = useEnrollStudent(token!, activeProfile?.id);
  const deleteEnrollmentMutation = useDeleteEnrollment(token!, activeProfile?.id);
  
  const loading = studentsLoading || enrollmentsLoading;

  // Derive class students from cached data with enrollment IDs
  const students = useMemo(() => {
    const classEnrollments = allEnrollments.filter((e) => e.class_id === classId);
    const enrollmentMap = new Map(classEnrollments.map(e => [e.student_id, e.id]));

    return allStudents
      .filter((s) => enrollmentMap.has(s.id))
      .map((s) => ({
        ...s,
        name: s.full_name,
        enrollmentId: enrollmentMap.get(s.id),
      }));
  }, [allStudents, allEnrollments, classId]);

  const [searchTerm, setSearchTerm] = useState("");
  const [newStudentName, setNewStudentName] = useState("");
  const [newStudentEmail, setNewStudentEmail] = useState("");
  const [isAdding, setIsAdding] = useState(false);

  const handleAddStudent = () => {
    if (!newStudentName.trim() || !token) return;

    const trimmedEmail = newStudentEmail.trim();
    const payload = {
      full_name: newStudentName,
      email: trimmedEmail || undefined,
      registration_code: Date.now().toString(),
      school_profile_id: activeProfile?.id,
    };

    // UI state reset immediately
    setIsAdding(false);
    setNewStudentName("");
    setNewStudentEmail("");

    // 1. Create student
    createStudentMutation.mutate(payload, {
      onSuccess: (created) => {
        // 2. Enroll in class
        enrollStudentMutation.mutate({
          classId,
          studentId: created.id,
          schoolProfileId: activeProfile?.id
        }, {
          onSuccess: () => {
            if (onRefresh) onRefresh();
          },
          onError: () => {
            alert("Aluno criado, mas erro ao matricular na turma.");
          }
        });
      },
      onError: (err) => {
        console.error("Failed to create student", err);
        alert("Erro ao criar aluno.");
        setIsAdding(true); // Re-open if failed
        setNewStudentName(payload.full_name);
        setNewStudentEmail(payload.email || "");
      }
    });
  };

  const handleRemoveStudent = (enrollmentId?: string) => {
    if (!enrollmentId) return;
    if (window.confirm("Deseja remover este aluno da turma?")) {
      deleteEnrollmentMutation.mutate(enrollmentId, {
        onError: () => {
          alert("Erro ao remover aluno da turma.");
        }
      });
    }
  };

  const filteredStudents = useMemo(() => {
    return students.filter((s) =>
      s.name.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [students, searchTerm]);

  return (
    <div className="flex flex-col h-full bg-(--card-bg)">
      <div className="p-6 border-b border-(--border-color)">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-xl font-bold text-(--text-primary)">
              Gerenciar Alunos
            </h2>
            <p className="text-sm text-(--text-tertiary)">{className}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-(--bg-accent) rounded-full transition text-(--text-tertiary)"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-(--text-tertiary)" />
            <input
              type="text"
              placeholder="Buscar aluno..."
              className="w-full pl-9 pr-4 py-2 border border-(--border-color) rounded-lg bg-(--input-bg) text-(--text-primary) outline-none focus:ring-2 focus:ring-(--primary-color)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button
            onClick={() => setIsAdding(true)}
            className="px-4 py-2 bg-(--primary-color) text-white rounded-lg font-medium hover:opacity-90 transition flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Adicionar
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {loading ? (
          <div className="text-center py-10 text-(--text-tertiary)">
            Carregando alunos...
          </div>
        ) : (
          <>
            {isAdding && (
              <div className="p-4 border border-(--primary-color) rounded-lg bg-(--bg-secondary) animate-in fade-in slide-in-from-top-2">
                <h3 className="text-sm font-bold mb-3 text-(--text-primary)">
                  Novo Aluno
                </h3>
                <div className="space-y-3">
                  <input
                    autoFocus
                    type="text"
                    placeholder="Nome completo"
                    className="w-full px-3 py-2 border border-(--border-color) rounded-lg bg-(--input-bg) text-(--text-primary)"
                    value={newStudentName}
                    onChange={(e) => setNewStudentName(e.target.value)}
                  />
                  <input
                    type="email"
                    placeholder="E-mail (opcional)"
                    className="w-full px-3 py-2 border border-(--border-color) rounded-lg bg-(--input-bg) text-(--text-primary)"
                    value={newStudentEmail}
                    onChange={(e) => setNewStudentEmail(e.target.value)}
                  />
                  <div className="flex gap-2 justify-end pt-2">
                    <button
                      onClick={() => setIsAdding(false)}
                      className="px-3 py-1 text-sm text-(--text-tertiary) hover:text-(--text-primary)"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleAddStudent}
                      disabled={!newStudentName.trim()}
                      className="px-4 py-1.5 bg-(--primary-color) text-white rounded-md text-sm font-medium disabled:opacity-50"
                    >
                      Confirmar
                    </button>
                  </div>
                </div>
              </div>
            )}

            {filteredStudents.length > 0 ? (
              filteredStudents.map((student) => (
                <div
                  key={student.id}
                  className="flex items-center justify-between p-3 border border-(--border-color) rounded-lg hover:bg-(--bg-accent) transition group"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-(--bg-secondary) flex items-center justify-center text-(--text-secondary)">
                      <User className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-medium text-(--text-primary)">
                        {student.name}
                      </p>
                      <p className="text-xs text-(--text-tertiary)">
                        {student.email}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleRemoveStudent(student.enrollmentId)}
                    className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full opacity-0 group-hover:opacity-100 transition"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))
            ) : (
              <div className="text-center py-10 text-(--text-tertiary)">
                {searchTerm
                  ? "Nenhum aluno encontrado."
                  : "Nenhum aluno cadastrado."}
              </div>
            )}
          </>
        )}
      </div>

      <div className="p-4 bg-(--bg-secondary) border-t border-(--border-color) text-center">
        <p className="text-xs text-(--text-tertiary)">
          Total: {students.length} alunos
        </p>
      </div>
    </div>
  );
}
